/**
 * NewConversationDialog
 * 
 * Dialog for starting a new conversation.
 * Allows selecting team members to message.
 */

import React, { useState } from 'react';
import { Users, X, Loader2, User, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { createDirectConversation, createGroupConversation } from '@/lib/firestore';
import { UserAvatar } from '@/components/shared';
import { useContactSelection } from './hooks/useContactSelection';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewConversationDialog({ open, onClose, user, onConversationCreated }) {
  const { isStudio } = useDesign();
  const {
    selectedUsers,
    searchQuery,
    setSearchQuery,
    searchInputRef,
    loading,
    error,
    filteredMembers,
    toggleUser,
    isSelected,
  } = useContactSelection({ open, user });

  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Reset group name when dialog closes
  React.useEffect(() => {
    if (!open) {
      setGroupName('');
      setCreateError(null);
    }
  }, [open]);

  // Create conversation
  const handleCreate = async () => {
    if (selectedUsers.length === 0) return;

    setCreating(true);
    setCreateError(null);

    try {
      const currentUser = {
        id: user.id,
        name: user.name || user.user_metadata?.name || user.email,
        email: user.email,
        avatar: user.avatar || null
      };

      let conversation;

      if (selectedUsers.length === 1) {
        conversation = await createDirectConversation(currentUser, selectedUsers[0]);
      } else {
        conversation = await createGroupConversation(
          currentUser, 
          selectedUsers, 
          groupName.trim() || null
        );
      }

      onConversationCreated(conversation);
    } catch (err) {
      console.error('Error creating conversation:', err);
      setCreateError('Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users data-ui="dialog-icon-tile" className="w-5 h-5 text-blue-500 dark:text-blue-400" />
            {isStudio ? 'New conversation' : 'New Conversation'}
          </DialogTitle>
        </DialogHeader>

        {/* Selected Users */}
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-100 dark:border-white/10">
            {selectedUsers.map(member => (
              <Badge
                key={member.id}
                variant="secondary"
                data-ui="rcp-chip"
                className="flex items-center gap-1 pl-1 pr-2 py-1"
              >
                <UserAvatar userId={member.id} name={member.name || member.email} avatarUrl={member.avatar} size="xs" />
                <span className="ml-1">{(member.name || member.email).split(' ')[0]}</span>
                <button
                  onClick={() => toggleUser(member)}
                  className="ml-1 hover:text-red-500"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Group Name Input - only show when 2+ users selected */}
        {selectedUsers.length >= 2 && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {isStudio ? 'Group name (optional)' : 'Group Name (optional)'}
            </label>
            <Input
              type="text"
              placeholder={`e.g., "${selectedUsers.slice(0, 2).map(u => (u.name || u.email).split(' ')[0]).join(', ')}..."`}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              maxLength={50}
            />
            <p className="text-xs text-slate-400 dark:text-slate-500">
              Leave empty to auto-generate from member names
            </p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={isStudio ? 'Search team members' : 'Search team members...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Member List */}
        <ScrollArea className="h-64">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400 dark:text-slate-500" />
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
              <User className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">
                {searchQuery ? 'No members found' : 'No team members yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredMembers.map(member => (
                <button
                  key={member.id}
                  onClick={() => toggleUser(member)}
                  data-ui="member-row"
                  data-on={isSelected(member.id) ? 'true' : undefined}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors
                    ${isSelected(member.id) 
                      ? 'bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900' 
                      : 'hover:bg-slate-50 dark:hover:bg-white/5 border border-transparent'
                    }
                  `}
                >
                  <UserAvatar userId={member.id} name={member.name || member.email} avatarUrl={member.avatar} size="md" />
                  <div className="flex-1 text-left">
                    <div className="font-medium text-slate-800 dark:text-slate-100">
                      {member.name || member.email}
                    </div>
                    {member.name && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">{member.email}</div>
                    )}
                  </div>
                  {isSelected(member.id) && (
                    <div data-ui="check-round" className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Error */}
        {(error || createError) && (
          <p className="text-sm text-red-500 dark:text-red-400">{error || createError}</p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-white/10">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={selectedUsers.length === 0 || creating}
            className="bg-blue-500 hover:bg-blue-600"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : selectedUsers.length > 1 ? (
              isStudio ? 'Create group' : 'Create Group'
            ) : (
              isStudio ? 'Start chat' : 'Start Chat'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
